/**
 * Priority discovery: each priority you inherit or discover is an item, and
 * what you will do with it (inherit, accelerate, fix, stop or new) is a value
 * on that item, not a separate list. Items raised in the listening tour can be
 * sorted here too.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import type { Item } from '@/types/prioritization/lifecycle';
import { Select } from '@/components/ds';
import { getAppDefinition } from '../registry';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton } from '../components/primitives';
import {
  DAY_101_DISPOSITIONS,
  DISPOSITIONS,
  LISTENING_TOUR_TAG,
  dispositionOf,
  withDisposition,
  type Disposition,
} from './first100Days';
import { useJourney } from './useJourney';
import NoMandate from './NoMandate';

const FIELD =
  'rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]';

export default function PriorityDiscoveryScreen() {
  const app = getAppDefinition('first-100-days')!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName);
  const items = useItemsStore((s) => s.items);
  const createItem = useItemsStore((s) => s.createItem);
  const updateItem = useItemsStore((s) => s.updateItem);
  const { loaded, transition } = useJourney();

  const [title, setTitle] = useState('');
  const [disposition, setDisposition] = useState<Disposition>('inherit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const back = { backTo: '/apps/first-100-days', backLabel: 'Back to the journey' };
  if (!loaded) {
    return (
      <AppShell app={app} {...back}>
        <p className="font-body text-sm text-[var(--text-ter)]">Reading your mandate…</p>
      </AppShell>
    );
  }
  if (!transition) {
    return (
      <AppShell app={app} {...back}>
        <NoMandate />
      </AppShell>
    );
  }

  const active = items.filter((item) => (item.exit_status ?? 'active') === 'active');
  const unsortedHeard = active.filter((item) => (item.tags ?? []).includes(LISTENING_TOUR_TAG) && !dispositionOf(item));
  const toDrive = active.filter((item) => DAY_101_DISPOSITIONS.includes(dispositionOf(item) as Disposition)).length;

  const add = async () => {
    if (!title.trim() || !userId) return;
    setBusy(true);
    setError(null);
    const ok = await createItem({
      title: title.trim(),
      exit_status: 'active',
      tags: withDisposition([], disposition),
      createdBy: userName || userId,
      workspace_id: getCurrentWorkspaceId() ?? undefined,
      originModule: 'mandate',
    } as Parameters<typeof createItem>[0]);
    setBusy(false);
    if (!ok) {
      setError('That priority could not be added. Try again.');
      return;
    }
    setTitle('');
  };

  const sort = (item: Item, next: Disposition) => void updateItem(item.id, { tags: withDisposition(item.tags ?? [], next) });

  return (
    <AppShell app={app} {...back}>
      <ScreenHeading title="Priority discovery" subtitle="Surface what you are inheriting and discovering, and say what you will do with each." />

      <AppCard className="mt-4">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="A priority you inherited or found"
            aria-label="Priority"
            className={`min-w-0 flex-1 ${FIELD}`}
          />
          <Select size="sm" className="!w-auto shrink-0" value={disposition} onChange={(e) => setDisposition(e.target.value as Disposition)} aria-label="What you will do with it">
            {DISPOSITIONS.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </Select>
          <SecondaryButton type="submit" disabled={busy || !title.trim()}>Add</SecondaryButton>
        </form>
        {error ? <p role="alert" className="mt-2 font-body text-sm text-[var(--coral)]">{error}</p> : null}
      </AppCard>

      {unsortedHeard.length > 0 ? (
        <section className="mt-6">
          <Eyebrow>Heard in the listening tour, not yet sorted</Eyebrow>
          <ul className="mt-2 space-y-2">
            {unsortedHeard.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                <span className="min-w-0 flex-1 font-body text-sm text-[var(--text-pri)]">{item.title}</span>
                <Select size="sm" className="!w-auto shrink-0" value="" onChange={(e) => sort(item, e.target.value as Disposition)} aria-label={`Sort ${item.title}`}>
                  <option value="">Sort it…</option>
                  {DISPOSITIONS.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.label}
                    </option>
                  ))}
                </Select>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {DISPOSITIONS.map((d) => {
        const members = active.filter((item) => dispositionOf(item) === d.id);
        return (
          <section key={d.id} className="mt-6">
            <Eyebrow>
              {d.label} · {members.length}
            </Eyebrow>
            <p className="mt-0.5 font-body text-xs text-[var(--text-ter)]">{d.description}</p>
            {members.length > 0 ? (
              <ul className="mt-2 space-y-2">
                {members.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                    <span className="min-w-0 flex-1 font-body text-sm text-[var(--text-pri)]">{item.title}</span>
                    <Select size="sm" className="!w-auto shrink-0" value={d.id} onChange={(e) => sort(item, e.target.value as Disposition)} aria-label={`Change what you will do with ${item.title}`}>
                      {DISPOSITIONS.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
        );
      })}

      <div className="mt-6">
        {toDrive >= 2 ? (
          <PrimaryButton onClick={() => navigate('/apps/first-100-days/day-101')}>Go to Day 101</PrimaryButton>
        ) : (
          <p className="font-body text-xs text-[var(--text-ter)]">
            Day 101 ranks your new, accelerate and fix priorities. Sort at least two of them to get there.
          </p>
        )}
      </div>
    </AppShell>
  );
}
